import React, { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import Header from './Header';
import Footer from './Footer';
import { ChevronDown, ChevronUp, HelpCircle, MessageCircleQuestion } from 'lucide-react';

const FAQsPage = () => {
  const { currentLanguage } = useLanguage();
  const [openIndex, setOpenIndex] = useState(0);

  const content = {
    en: {
      badge: "Support",
      title: "Frequently Asked Questions",
      subtitle: "Find answers to common questions about our products and services",
      faqs: [
        {
          question: "Who can purchase products from PenLab Peptide?",
          answer: "PenLab Peptide exclusively supplies licensed medical professionals and authorized institutions. This includes research laboratories, medical clinics, hospitals, universities, pharmaceutical companies, and other licensed entities. To place an order, you must register an account and complete our KYC (Know Your Customer) verification process. You will need to provide your organization name, type, and professional credentials. Our team will review your submission, and once approved (typically within 24-48 hours), you'll gain access to our full product catalog. This verification ensures compliance with regulations and that our research compounds are handled by qualified professionals."
        },
        {
          question: "What are research peptides?",
          answer: "Research peptides are short chains of amino acids used exclusively for scientific research and laboratory studies. They are synthesized to study various biological processes and are not intended for human consumption or therapeutic use."
        },
        {
          question: "Are your products suitable for human use?",
          answer: "No. All our products are strictly for research purposes only and are not intended for human consumption. They are sold exclusively to qualified researchers and laboratories for in-vitro and research applications."
        },
        {
          question: "What is the purity of your peptides?",
          answer: "All our peptides meet a minimum purity standard of 99%. Each batch undergoes rigorous HPLC testing and comes with a Certificate of Analysis (COA) documenting the exact purity levels."
        },
        {
          question: "What makes your stabilized pen format unique?",
          answer: "Our stabilized pen format is an innovative delivery system that maintains peptide stability at room temperature for short periods. This pre-mixed, ready-to-use format eliminates the need for reconstitution and ensures consistent dosing for research applications."
        },
        {
          question: "How should I store the products?",
          answer: "For optimal stability, products should be stored in a refrigerator (2-8°C). Our stabilized formulas can tolerate short-term temperature deviations during shipping, but long-term storage should be refrigerated."
        },
        {
          question: "What are your shipping options?",
          answer: "We offer DHL Express shipping with delivery in 3-5 business days. All packages are shipped in temperature-controlled packaging when necessary."
        },
        {
          question: "What payment methods do you accept?",
          answer: "We accept Bank Transfer only. After you submit an order enquiry, our team will review your credentials and perform KYC verification (typically 24-48 hours). Once approved, we will issue an invoice with our bank details. Your order will be processed and shipped within 3-5 working days after we confirm receipt of your payment."
        },
        {
          question: "Do you ship internationally?",
          answer: "Yes! We deliver anywhere in the world. Please contact us for specific shipping inquiries regarding your location."
        }
      ]
    },
    es: {
      badge: "Soporte",
      title: "Preguntas Frecuentes",
      subtitle: "Encuentre respuestas a preguntas comunes sobre nuestros productos y servicios",
      faqs: [
        {
          question: "¿Quién puede comprar productos de PenLab Peptide?",
          answer: "PenLab Peptide suministra exclusivamente a profesionales médicos autorizados e instituciones autorizadas. Esto incluye laboratorios de investigación, clínicas médicas, hospitales, universidades, empresas farmacéuticas y otras entidades autorizadas. Para realizar un pedido, debe registrar una cuenta y completar nuestro proceso de verificación KYC (Conozca a su Cliente). Deberá proporcionar el nombre de su organización, tipo y credenciales profesionales. Nuestro equipo revisará su solicitud y, una vez aprobada (normalmente en 24-48 horas), tendrá acceso a nuestro catálogo completo de productos. Esta verificación garantiza el cumplimiento de las regulaciones y que nuestros compuestos de investigación sean manejados por profesionales calificados."
        },
        {
          question: "¿Qué son los péptidos de investigación?",
          answer: "Los péptidos de investigación son cadenas cortas de aminoácidos utilizados exclusivamente para investigación científica y estudios de laboratorio. Se sintetizan para estudiar varios procesos biológicos y no están destinados al consumo humano ni al uso terapéutico."
        },
        {
          question: "¿Sus productos son aptos para uso humano?",
          answer: "No. Todos nuestros productos son estrictamente solo para fines de investigación y no están destinados al consumo humano. Se venden exclusivamente a investigadores calificados y laboratorios para aplicaciones in-vitro y de investigación."
        },
        {
          question: "¿Cuál es la pureza de sus péptidos?",
          answer: "Todos nuestros péptidos cumplen con un estándar mínimo de pureza del 99%. Cada lote se somete a pruebas rigurosas de HPLC y viene con un Certificado de Análisis (COA) que documenta los niveles exactos de pureza."
        },
        {
          question: "¿Qué hace único su formato de pluma estabilizada?",
          answer: "Nuestro formato de pluma estabilizada es un sistema de entrega innovador que mantiene la estabilidad del péptido a temperatura ambiente durante períodos cortos. Este formato premezclado y listo para usar elimina la necesidad de reconstitución y garantiza una dosificación consistente para aplicaciones de investigación."
        },
        {
          question: "¿Cómo debo almacenar los productos?",
          answer: "Para una estabilidad óptima, los productos deben almacenarse en un refrigerador (2-8°C). Nuestras fórmulas estabilizadas pueden tolerar desviaciones de temperatura a corto plazo durante el envío, pero el almacenamiento a largo plazo debe ser refrigerado."
        },
        {
          question: "¿Cuáles son sus opciones de envío?",
          answer: "Ofrecemos envío DHL Express con entrega en 3-5 días hábiles. Todos los paquetes se envían en embalaje con temperatura controlada cuando es necesario."
        },
        {
          question: "¿Qué métodos de pago aceptan?",
          answer: "Aceptamos solo Transferencia Bancaria. Después de enviar una solicitud de pedido, nuestro equipo revisará sus credenciales y realizará la verificación KYC (normalmente 24-48 horas). Una vez aprobado, emitiremos una factura con nuestros datos bancarios. Su pedido será procesado y enviado dentro de 3-5 días hábiles después de confirmar la recepción de su pago."
        },
        {
          question: "¿Realizan envíos internacionales?",
          answer: "¡Sí! Realizamos envíos a cualquier parte del mundo. Contáctenos para consultas específicas de envío sobre su ubicación."
        }
      ]
    },
    ru: {
      badge: "Поддержка",
      title: "Часто задаваемые вопросы",
      subtitle: "Ответы на популярные вопросы о нашей продукции и услугах",
      faqs: [
        {
          question: "Кто может приобретать продукцию PenLab Peptide?",
          answer: "PenLab Peptide осуществляет поставки исключительно лицензированным медицинским специалистам и аккредитованным учреждениям. К ним относятся исследовательские лаборатории, медицинские клиники, больницы, университеты, фармацевтические компании и другие организации с соответствующей лицензией. Для оформления заказа необходимо зарегистрировать учётную запись и пройти процедуру верификации KYC (Знай своего клиента). Вам потребуется указать название организации, её тип и предоставить профессиональные документы. Наши специалисты рассмотрят заявку, и после одобрения (обычно в течение 24-48 часов) вы получите доступ к полному каталогу продукции. Верификация обеспечивает соблюдение нормативных требований и гарантирует, что наши исследовательские соединения используются квалифицированными специалистами."
        },
        {
          question: "Что такое исследовательские пептиды?",
          answer: "Исследовательские пептиды, это короткие цепочки аминокислот, предназначенные исключительно для научных исследований и лабораторных экспериментов. Они синтезируются для изучения различных биологических процессов и не предназначены для употребления человеком или терапевтического применения."
        },
        {
          question: "Можно ли использовать вашу продукцию для человека?",
          answer: "Нет. Вся наша продукция предназначена строго для исследовательских целей и не подлежит употреблению человеком. Она реализуется исключительно квалифицированным исследователям и лабораториям для проведения экспериментов in vitro и научных исследований."
        },
        {
          question: "Какова чистота ваших пептидов?",
          answer: "Все наши пептиды соответствуют минимальному стандарту чистоты 99%. Каждая партия проходит тщательное тестирование методом ВЭЖХ (высокоэффективной жидкостной хроматографии) и сопровождается сертификатом анализа (COA) с указанием точных показателей чистоты."
        },
        {
          question: "В чём уникальность вашего формата стабилизированной шприц-ручки?",
          answer: "Наша стабилизированная шприц-ручка, это инновационная система доставки, обеспечивающая сохранение стабильности пептида при комнатной температуре в течение короткого периода. Готовый к применению формат исключает необходимость разведения и гарантирует точность дозирования в исследовательских целях."
        },
        {
          question: "Как следует хранить продукцию?",
          answer: "Для оптимальной сохранности продукцию рекомендуется хранить в холодильнике при температуре 2-8°C. Наши стабилизированные формулы выдерживают кратковременные температурные колебания при транспортировке, однако для длительного хранения необходимо соблюдать температурный режим холодильника."
        },
        {
          question: "Какие варианты доставки вы предлагаете?",
          answer: "Мы осуществляем экспресс-доставку курьерской службой DHL со сроком 3-5 рабочих дней. При необходимости все посылки отправляются в термоупаковке с контролем температуры."
        },
        {
          question: "Какие способы оплаты вы принимаете?",
          answer: "Мы принимаем оплату только банковским переводом. После отправки заявки на заказ наша команда проверит ваши данные и проведёт верификацию KYC (обычно 24-48 часов). После одобрения мы выставим счёт с банковскими реквизитами. Ваш заказ будет обработан и отправлен в течение 3-5 рабочих дней после подтверждения поступления оплаты."
        },
        {
          question: "Осуществляете ли вы международную доставку?",
          answer: "Да! Мы доставляем заказы в любую точку мира. Для уточнения деталей доставки в ваш регион, пожалуйста, свяжитесь с нами."
        }
      ]
    },
    de: {
      badge: "Support",
      title: "Häufig gestellte Fragen",
      subtitle: "Finden Sie Antworten auf häufige Fragen zu unseren Produkten und Dienstleistungen",
      faqs: [
        {
          question: "Wer kann Produkte von PenLab Peptide erwerben?",
          answer: "PenLab Peptide beliefert ausschließlich approbierte medizinische Fachkräfte und autorisierte Institutionen. Dazu gehören Forschungslabore, medizinische Kliniken, Krankenhäuser, Universitäten, Pharmaunternehmen und andere lizenzierte Einrichtungen. Um eine Bestellung aufzugeben, müssen Sie ein Konto registrieren und unseren KYC-(Know Your Customer)-Verifizierungsprozess abschließen. Sie müssen dabei den Namen und Typ Ihrer Organisation sowie Ihre beruflichen Qualifikationen angeben. Unser Team prüft Ihre Angaben, und nach Freigabe (in der Regel innerhalb von 24-48 Stunden) erhalten Sie Zugriff auf unseren vollständigen Produktkatalog. Diese Verifizierung stellt die Einhaltung von Vorschriften sicher und gewährleistet, dass unsere Forschungswirkstoffe von qualifizierten Fachkräften gehandhabt werden."
        },
        {
          question: "Was sind Forschungspeptide?",
          answer: "Forschungspeptide sind kurze Ketten von Aminosäuren, die ausschließlich für wissenschaftliche Forschung und Laborstudien verwendet werden. Sie werden synthetisiert, um verschiedene biologische Prozesse zu untersuchen, und sind nicht für den menschlichen Verzehr oder therapeutische Anwendungen bestimmt."
        },
        {
          question: "Sind Ihre Produkte für den menschlichen Gebrauch geeignet?",
          answer: "Nein. Alle unsere Produkte sind ausschließlich für Forschungszwecke bestimmt und nicht für den menschlichen Verzehr vorgesehen. Sie werden ausschließlich an qualifizierte Forschende und Labore für In-vitro- und Forschungsanwendungen verkauft."
        },
        {
          question: "Wie hoch ist die Reinheit Ihrer Peptide?",
          answer: "Alle unsere Peptide erfüllen einen Mindestreinheitsstandard von 99 %. Jede Charge durchläuft strenge HPLC-Tests und wird mit einem Analysenzertifikat (Certificate of Analysis, COA) geliefert, das die exakten Reinheitsgrade dokumentiert."
        },
        {
          question: "Was macht Ihr stabilisiertes Pen-Format einzigartig?",
          answer: "Unser stabilisiertes Pen-Format ist ein innovatives Applikationssystem, das die Peptidstabilität bei Raumtemperatur über kurze Zeiträume aufrechterhält. Dieses vorgemischte, gebrauchsfertige Format macht eine Rekonstitution überflüssig und gewährleistet eine konsistente Dosierung für Forschungsanwendungen."
        },
        {
          question: "Wie sollte ich die Produkte lagern?",
          answer: "Für optimale Stabilität sollten Produkte in einem Kühlschrank (2-8 °C) gelagert werden. Unsere stabilisierten Formulierungen können kurzfristige Temperaturschwankungen während des Versands tolerieren, sollten aber für die Langzeitlagerung gekühlt werden."
        },
        {
          question: "Welche Versandoptionen bieten Sie an?",
          answer: "Wir bieten DHL Express-Versand mit Zustellung innerhalb von 3-5 Werktagen an. Alle Sendungen werden bei Bedarf in temperaturkontrollierter Verpackung verschickt."
        },
        {
          question: "Welche Zahlungsmethoden akzeptieren Sie?",
          answer: "Wir akzeptieren ausschließlich Banküberweisung. Nachdem Sie eine Bestellanfrage eingereicht haben, prüft unser Team Ihre Qualifikationen und führt eine KYC-Verifizierung durch (in der Regel 24-48 Stunden). Nach Freigabe stellen wir eine Rechnung mit unseren Bankdaten aus. Ihre Bestellung wird innerhalb von 3-5 Werktagen nach Bestätigung des Zahlungseingangs bearbeitet und versandt."
        },
        {
          question: "Versenden Sie international?",
          answer: "Ja! Wir liefern weltweit. Bitte kontaktieren Sie uns für konkrete Versandfragen zu Ihrem Standort."
        }
      ]
    },
    fr: {
      badge: "Support",
      title: "Foire aux questions",
      subtitle: "Trouvez des réponses aux questions fréquentes sur nos produits et services",
      faqs: [
        {
          question: "Qui peut acheter des produits auprès de PenLab Peptide ?",
          answer: "PenLab Peptide fournit exclusivement des professionnels de santé agréés et des institutions autorisées. Cela inclut les laboratoires de recherche, cliniques médicales, hôpitaux, universités, entreprises pharmaceutiques et autres entités agréées. Pour passer une commande, vous devez créer un compte et compléter notre procédure de vérification KYC (Know Your Customer). Vous devrez fournir le nom de votre organisation, son type, ainsi que vos qualifications professionnelles. Notre équipe examinera votre dossier et, une fois approuvé (généralement sous 24 à 48 heures), vous aurez accès à l’intégralité de notre catalogue de produits. Cette vérification garantit la conformité réglementaire et assure que nos composés de recherche sont manipulés par des professionnels qualifiés."
        },
        {
          question: "Que sont les peptides de recherche ?",
          answer: "Les peptides de recherche sont de courtes chaînes d’acides aminés utilisées exclusivement pour la recherche scientifique et les études en laboratoire. Ils sont synthétisés pour étudier divers processus biologiques et ne sont pas destinés à la consommation humaine ni à un usage thérapeutique."
        },
        {
          question: "Vos produits sont-ils adaptés à l’usage humain ?",
          answer: "Non. Tous nos produits sont strictement destinés à des fins de recherche uniquement et ne sont pas conçus pour la consommation humaine. Ils sont vendus exclusivement à des chercheurs qualifiés et à des laboratoires pour des applications in vitro et de recherche."
        },
        {
          question: "Quel est le degré de pureté de vos peptides ?",
          answer: "Tous nos peptides répondent à une norme de pureté minimale de 99 %. Chaque lot fait l’objet de tests HPLC rigoureux et est accompagné d’un certificat d’analyse (COA) documentant précisément les niveaux de pureté."
        },
        {
          question: "Qu’est-ce qui rend votre format stylo stabilisé unique ?",
          answer: "Notre format stylo stabilisé est un système d’administration innovant qui maintient la stabilité des peptides à température ambiante pendant de courtes périodes. Ce format prémélangé et prêt à l’emploi élimine la nécessité de reconstitution et assure un dosage cohérent pour les applications de recherche."
        },
        {
          question: "Comment dois-je stocker les produits ?",
          answer: "Pour une stabilité optimale, les produits doivent être conservés au réfrigérateur (2-8 °C). Nos formules stabilisées peuvent tolérer de légères variations de température à court terme pendant le transport, mais le stockage à long terme doit se faire au réfrigérateur."
        },
        {
          question: "Quelles sont vos options de livraison ?",
          answer: "Nous proposons la livraison par DHL Express avec un délai de 3 à 5 jours ouvrables. Tous les colis sont expédiés dans un emballage à température contrôlée lorsque cela est nécessaire."
        },
        {
          question: "Quels moyens de paiement acceptez-vous ?",
          answer: "Nous acceptons uniquement le virement bancaire. Après l’envoi de votre demande de commande, notre équipe examinera vos références et effectuera la vérification KYC (généralement sous 24 à 48 heures). Une fois votre dossier approuvé, nous émettrons une facture avec nos coordonnées bancaires. Votre commande sera traitée et expédiée dans un délai de 3 à 5 jours ouvrables après confirmation de la réception de votre paiement."
        },
        {
          question: "Livrez-vous à l’international ?",
          answer: "Oui ! Nous livrons partout dans le monde. Veuillez nous contacter pour toute question spécifique concernant la livraison vers votre localisation."
        }
      ]
    },
    it: {
      badge: "Supporto",
      title: "Domande frequenti",
      subtitle: "Trova le risposte alle domande più comuni sui nostri prodotti e servizi",
      faqs: [
        {
          question: "Chi può acquistare prodotti da PenLab Peptide?",
          answer: "PenLab Peptide fornisce in via esclusiva professionisti medici autorizzati e istituzioni accreditate. Questo include laboratori di ricerca, cliniche mediche, ospedali, università, aziende farmaceutiche e altre entità autorizzate. Per effettuare un ordine, è necessario registrare un account e completare il nostro processo di verifica KYC (Know Your Customer). Dovrai fornire il nome della tua organizzazione, il tipo e le credenziali professionali. Il nostro team esaminerà la tua richiesta e, una volta approvata (tipicamente entro 24-48 ore), avrai accesso al nostro catalogo completo di prodotti. Questa verifica garantisce la conformità alle normative e che i nostri composti per la ricerca siano gestiti da professionisti qualificati."
        },
        {
          question: "Cosa sono i peptidi di ricerca?",
          answer: "I peptidi di ricerca sono brevi catene di amminoacidi utilizzate esclusivamente per la ricerca scientifica e gli studi di laboratorio. Vengono sintetizzati per studiare vari processi biologici e non sono destinati al consumo umano o all’uso terapeutico."
        },
        {
          question: "I vostri prodotti sono adatti all’uso umano?",
          answer: "No. Tutti i nostri prodotti sono destinati esclusivamente a scopi di ricerca e non sono destinati al consumo umano. Sono venduti esclusivamente a ricercatori qualificati e laboratori per applicazioni in vitro e di ricerca."
        },
        {
          question: "Qual è il grado di purezza dei vostri peptidi?",
          answer: "Tutti i nostri peptidi soddisfano uno standard minimo di purezza del 99%. Ogni lotto è sottoposto a rigorosi test HPLC ed è accompagnato da un Certificato di Analisi (COA) che documenta i livelli esatti di purezza."
        },
        {
          question: "Cosa rende unico il vostro formato penna stabilizzato?",
          answer: "Il nostro formato penna stabilizzato è un sistema di somministrazione innovativo che mantiene la stabilità del peptide a temperatura ambiente per brevi periodi. Questo formato pre-miscelato e pronto all’uso elimina la necessità di ricostituzione e garantisce un dosaggio uniforme per le applicazioni di ricerca."
        },
        {
          question: "Come devo conservare i prodotti?",
          answer: "Per una stabilità ottimale, i prodotti devono essere conservati in frigorifero (2-8°C). Le nostre formule stabilizzate possono tollerare deviazioni di temperatura a breve termine durante la spedizione, ma la conservazione a lungo termine deve avvenire in frigorifero."
        },
        {
          question: "Quali sono le vostre opzioni di spedizione?",
          answer: "Offriamo spedizione con DHL Express con consegna in 3-5 giorni lavorativi. Tutti i pacchi vengono spediti in imballaggi a temperatura controllata quando necessario."
        },
        {
          question: "Quali metodi di pagamento accettate?",
          answer: "Accettiamo esclusivamente Bonifico Bancario. Dopo che avrai inviato una richiesta d’ordine, il nostro team verificherà le tue credenziali ed effettuerà la verifica KYC (tipicamente 24-48 ore). Una volta approvata, emetteremo una fattura con i nostri dati bancari. Il tuo ordine sarà elaborato e spedito entro 3-5 giorni lavorativi dopo che avremo confermato la ricezione del pagamento."
        },
        {
          question: "Effettuate spedizioni internazionali?",
          answer: "Sì! Consegniamo in qualsiasi parte del mondo. Contattaci per richieste specifiche di spedizione relative alla tua posizione."
        }
      ]
    },
    pl: {
      badge: "Wsparcie",
      title: "Najczęściej zadawane pytania",
      subtitle: "Znajdź odpowiedzi na najczęściej zadawane pytania dotyczące naszych produktów i usług",
      faqs: [
        {
          question: "Kto może kupować produkty od PenLab Peptide?",
          answer: "PenLab Peptide dostarcza produkty wyłącznie licencjonowanym specjalistom medycznym oraz autoryzowanym instytucjom. Obejmuje to laboratoria badawcze, kliniki medyczne, szpitale, uniwersytety, firmy farmaceutyczne i inne licencjonowane podmioty. Aby złożyć zamówienie, musisz zarejestrować konto i ukończyć nasz proces weryfikacji KYC (Know Your Customer). Konieczne będzie podanie nazwy i typu organizacji oraz kwalifikacji zawodowych. Nasz zespół przeanalizuje Twoje zgłoszenie i po jego zatwierdzeniu (zwykle w ciągu 24-48 godzin) uzyskasz dostęp do naszego pełnego katalogu produktów. Taka weryfikacja zapewnia zgodność z przepisami oraz gwarantuje, że nasze związki badawcze są obsługiwane przez wykwalifikowanych specjalistów."
        },
        {
          question: "Czym są peptydy badawcze?",
          answer: "Peptydy badawcze to krótkie łańcuchy aminokwasów stosowane wyłącznie do badań naukowych i prac laboratoryjnych. Są syntetyzowane w celu badania różnych procesów biologicznych i nie są przeznaczone do spożycia przez ludzi ani do zastosowań terapeutycznych."
        },
        {
          question: "Czy wasze produkty nadają się do stosowania u ludzi?",
          answer: "Nie. Wszystkie nasze produkty są przeznaczone wyłącznie do celów badawczych i nie są przeznaczone do spożycia przez ludzi. Są sprzedawane wyłącznie wykwalifikowanym naukowcom i laboratoriom do zastosowań in vitro oraz badań naukowych."
        },
        {
          question: "Jaka jest czystość waszych peptydów?",
          answer: "Wszystkie nasze peptydy spełniają minimalny standard czystości na poziomie 99%. Każda partia poddawana jest rygorystycznym testom HPLC i dostarczana z Certyfikatem Analizy (COA), który dokumentuje dokładny poziom czystości."
        },
        {
          question: "Co wyróżnia wasz stabilizowany format pena?",
          answer: "Nasz stabilizowany format pena to innowacyjny system podawania, który utrzymuje stabilność peptydu w temperaturze pokojowej przez krótkie okresy. Ten wstępnie przygotowany, gotowy do użycia format eliminuje konieczność rekonstytucji i zapewnia spójne dawkowanie w zastosowaniach badawczych."
        },
        {
          question: "Jak powinienem przechowywać produkty?",
          answer: "Dla optymalnej stabilności produkty powinny być przechowywane w lodówce (2-8°C). Nasze stabilizowane formuły mogą tolerować krótkotrwałe odchylenia temperatury podczas wysyłki, jednak długoterminowe przechowywanie powinno odbywać się w warunkach chłodniczych."
        },
        {
          question: "Jakie są wasze opcje wysyłki?",
          answer: "Oferujemy wysyłkę DHL Express z dostawą w ciągu 3-5 dni roboczych. Wszystkie przesyłki są wysyłane w opakowaniach z kontrolą temperatury, jeśli jest to konieczne."
        },
        {
          question: "Jakie metody płatności akceptujecie?",
          answer: "Akceptujemy wyłącznie przelew bankowy. Po przesłaniu zapytania ofertowego nasz zespół sprawdzi Twoje kwalifikacje i przeprowadzi weryfikację KYC (zwykle 24-48 godzin). Po zatwierdzeniu wystawimy fakturę z naszymi danymi bankowymi. Twoje zamówienie zostanie zrealizowane i wysłane w ciągu 3-5 dni roboczych po potwierdzeniu otrzymania płatności."
        },
        {
          question: "Czy wysyłacie produkty za granicę?",
          answer: "Tak! Dostarczamy produkty na cały świat. Skontaktuj się z nami w celu uzyskania szczegółowych informacji dotyczących wysyłki do Twojej lokalizacji."
        }
      ]
    },
    nl: {
      badge: "Support",
      title: "Veelgestelde vragen",
      subtitle: "Vind antwoorden op veelgestelde vragen over onze producten en diensten",
      faqs: [
        {
          question: "Wie kan producten van PenLab Peptide kopen?",
          answer: "PenLab Peptide levert exclusief aan erkende medische professionals en geautoriseerde instellingen. Dit omvat onderzoekslaboratoria, medische klinieken, ziekenhuizen, universiteiten, farmaceutische bedrijven en andere erkende entiteiten. Om een bestelling te plaatsen, moet u een account registreren en ons KYC-proces (Know Your Customer) doorlopen. U moet de naam en het type van uw organisatie en uw professionele referenties opgeven. Ons team beoordeelt uw aanvraag, en zodra deze is goedgekeurd (meestal binnen 24-48 uur), krijgt u toegang tot onze volledige productcatalogus. Deze verificatie zorgt voor naleving van de regelgeving en waarborgt dat onze onderzoeksstoffen worden behandeld door gekwalificeerde professionals."
        },
        {
          question: "Wat zijn onderzoekspeptiden?",
          answer: "Onderzoekspeptiden zijn korte ketens van aminozuren die uitsluitend worden gebruikt voor wetenschappelijk onderzoek en laboratoriumstudies. Ze worden gesynthetiseerd om verschillende biologische processen te bestuderen en zijn niet bedoeld voor menselijke consumptie of therapeutisch gebruik."
        },
        {
          question: "Zijn uw producten geschikt voor menselijk gebruik?",
          answer: "Nee. Al onze producten zijn strikt uitsluitend voor onderzoeksdoeleinden en zijn niet bedoeld voor menselijke consumptie. Ze worden uitsluitend verkocht aan gekwalificeerde onderzoekers en laboratoria voor in-vitro- en onderzoeks-toepassingen."
        },
        {
          question: "Wat is de zuiverheid van uw peptiden?",
          answer: "Al onze peptiden voldoen aan een minimale zuiverheidsnorm van 99%. Elke batch ondergaat strikte HPLC-testen en wordt geleverd met een analysecertificaat (Certificate of Analysis, COA) waarin de exacte zuiverheidsniveaus worden gedocumenteerd."
        },
        {
          question: "Wat maakt uw gestabiliseerde penformaat uniek?",
          answer: "Ons gestabiliseerde penformaat is een innovatief toedieningssysteem dat de stabiliteit van peptiden gedurende korte periodes bij kamertemperatuur behoudt. Dit voorgevulde, gebruiksklare formaat elimineert de noodzaak van reconstitutie en zorgt voor consistente dosering voor onderzoeksdoeleinden."
        },
        {
          question: "Hoe moet ik de producten bewaren?",
          answer: "Voor optimale stabiliteit moeten producten in een koelkast (2-8°C) worden bewaard. Onze gestabiliseerde formules kunnen kortdurende temperatuurschommelingen tijdens verzending verdragen, maar langdurige opslag moet in de koelkast plaatsvinden."
        },
        {
          question: "Wat zijn uw verzendopties?",
          answer: "Wij bieden DHL Express-verzending met levering binnen 3-5 werkdagen. Alle pakketten worden indien nodig verzonden in temperatuur-gecontroleerde verpakkingen."
        },
        {
          question: "Welke betaalmethoden accepteert u?",
          answer: "Wij accepteren uitsluitend bankoverschrijving. Nadat u een bestelaanvraag hebt ingediend, beoordeelt ons team uw referenties en voert KYC-verificatie uit (meestal 24-48 uur). Zodra u bent goedgekeurd, sturen wij een factuur met onze bankgegevens. Uw bestelling wordt verwerkt en verzonden binnen 3-5 werkdagen nadat wij de ontvangst van uw betaling hebben bevestigd."
        },
        {
          question: "Verstuurt u internationaal?",
          answer: "Ja! Wij leveren overal ter wereld. Neem contact met ons op voor specifieke verzendvragen met betrekking tot uw locatie."
        }
      ]
    },
    pt: {
      badge: "Suporte",
      title: "Perguntas Frequentes",
      subtitle: "Encontre respostas para perguntas comuns sobre nossos produtos e serviços",
      faqs: [
        {
          question: "Quem pode comprar produtos da PenLab Peptide?",
          answer: "A PenLab Peptide fornece exclusivamente para profissionais médicos licenciados e instituições autorizadas. Isso inclui laboratórios de pesquisa, clínicas médicas, hospitais, universidades, empresas farmacêuticas e outras entidades licenciadas. Para fazer um pedido, você deve registrar uma conta e concluir nosso processo de verificação KYC (Know Your Customer). Você precisará fornecer o nome da sua organização, tipo e credenciais profissionais. Nossa equipe analisará sua solicitação e, uma vez aprovada (normalmente em 24-48 horas), você terá acesso ao nosso catálogo completo de produtos. Essa verificação garante a conformidade com as regulamentações e que nossos compostos de pesquisa sejam manuseados por profissionais qualificados."
        },
        {
          question: "O que são peptídeos de pesquisa?",
          answer: "Peptídeos de pesquisa são cadeias curtas de aminoácidos usados exclusivamente para pesquisa científica e estudos laboratoriais. Eles são sintetizados para estudar vários processos biológicos e não são destinados ao consumo humano ou uso terapêutico."
        },
        {
          question: "Seus produtos são adequados para uso humano?",
          answer: "Não. Todos os nossos produtos são estritamente para fins de pesquisa e não são destinados ao consumo humano. Eles são vendidos exclusivamente para pesquisadores qualificados e laboratórios para aplicações in vitro e de pesquisa."
        },
        {
          question: "Qual é a pureza de seus peptídeos?",
          answer: "Todos os nossos peptídeos atendem a um padrão mínimo de pureza de 99%. Cada lote passa por rigorosos testes de HPLC e é acompanhado de um Certificado de Análise (COA) documentando os níveis exatos de pureza."
        },
        {
          question: "O que torna seu formato em caneta estabilizada único?",
          answer: "Nosso formato em caneta estabilizada é um sistema de administração inovador que mantém a estabilidade do peptídeo em temperatura ambiente por curtos períodos. Esse formato pré-misturado e pronto para uso elimina a necessidade de reconstituição e garante dosagem consistente para aplicações de pesquisa."
        },
        {
          question: "Como devo armazenar os produtos?",
          answer: "Para estabilidade ideal, os produtos devem ser armazenados em geladeira (2-8°C). Nossas fórmulas estabilizadas podem tolerar desvios de temperatura de curto prazo durante o envio, mas o armazenamento de longo prazo deve ser refrigerado."
        },
        {
          question: "Quais são as suas opções de envio?",
          answer: "Oferecemos envio via DHL Express com entrega em 3-5 dias úteis. Todos os pacotes são enviados em embalagens com controle de temperatura quando necessário."
        },
        {
          question: "Quais métodos de pagamento vocês aceitam?",
          answer: "Aceitamos somente Transferência Bancária. Depois que você enviar uma solicitação de pedido, nossa equipe analisará suas credenciais e realizará a verificação KYC (normalmente 24-48 horas). Uma vez aprovado, emitiremos uma fatura com nossos dados bancários. Seu pedido será processado e enviado em até 3-5 dias úteis após confirmarmos o recebimento do seu pagamento."
        },
        {
          question: "Vocês fazem envios internacionais?",
          answer: "Sim! Entregamos em qualquer lugar do mundo. Entre em contato conosco para dúvidas específicas de envio relacionadas à sua localização."
        }
      ]
    },
  };

  const c = content[currentLanguage] || content.en;

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? -1 : index);
  };

  // FAQPage JSON-LD injection (iter-63, Grok audit follow-up).
  // Wires every Q/A pair into structured data so Google can render the
  // FAQ rich result (collapsible accordion in SERPs, boosts CTR by
  // ~15-30% on FAQ-style queries). Locale-aware: the schema rebuilds
  // whenever currentLanguage changes so each /{lang}/faqs URL gets
  // native-language Q/A pairs in the rich result. Tagged data-plp-faq
  // to keep it idempotent on re-renders.
  useEffect(() => {
    const ld = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": (c.faqs || []).map((f) => ({
        "@type": "Question",
        "name": f.question,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": f.answer,
        },
      })),
    };
    try {
      document.querySelectorAll('script[data-plp-faq="1"]').forEach((el) => el.remove());
      const s = document.createElement('script');
      s.setAttribute('type', 'application/ld+json');
      s.setAttribute('data-plp-faq', '1');
      s.textContent = JSON.stringify(ld);
      document.head.appendChild(s);
      return () => {
        document.querySelectorAll('script[data-plp-faq="1"]').forEach((el) => el.remove());
      };
    } catch (_) { return undefined; }
  }, [currentLanguage, c.faqs]);

  return (
    <div className="min-h-screen bg-capsule-bg">
      <Header />

      {/* Hero Banner - Capsule 8 Design */}
      <div className="relative overflow-hidden bg-gradient-to-br from-gray-900 via-gray-800 to-capsule-teal-dark">
        {/* Hero Image Background */}
        <div className="absolute inset-0 opacity-30">
          <img 
            src="https://static.prod-images.emergentagent.com/jobs/bf28a057-bcbd-40cd-b04d-2b662e783ae3/images/98253405669f068f1322fa684a9445aa37d2e977632f4919bbb3df03430b56ef.png"
            alt="FAQ Background"
            className="w-full h-full object-cover"
          />
        </div>
        
        {/* DNA Decoration */}
        <div className="absolute right-0 top-0 bottom-0 w-96 opacity-20">
          <svg viewBox="0 0 200 400" fill="none" className="w-full h-full">
            <defs>
              <linearGradient id="faqGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#14B8A6" />
                <stop offset="100%" stopColor="#22D3EE" />
              </linearGradient>
            </defs>
            <path d="M50 0 Q120 50 50 100 Q-20 150 50 200 Q120 250 50 300 Q-20 350 50 400" 
                  stroke="url(#faqGradient)" strokeWidth="3" fill="none"/>
            <path d="M150 0 Q80 50 150 100 Q220 150 150 200 Q80 250 150 300 Q220 350 150 400" 
                  stroke="url(#faqGradient)" strokeWidth="3" fill="none"/>
          </svg>
        </div>
        
        {/* Floating spheres */}
        <div className="absolute top-20 right-32 w-20 h-20 rounded-full bg-gradient-to-br from-capsule-rose-gold/30 to-capsule-salmon/20 blur-xl"></div>
        <div className="absolute bottom-20 right-48 w-16 h-16 rounded-full bg-gradient-to-br from-capsule-teal/30 to-capsule-aqua/20 blur-xl"></div>
        
        <div className="relative max-w-7xl mx-auto px-4 py-16 md:py-24">
          <div className="text-center max-w-3xl mx-auto">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-md px-4 py-2 rounded-full mb-6 border border-white/20">
              <HelpCircle className="w-4 h-4 text-capsule-aqua" />
              <span className="text-sm font-medium text-capsule-aqua">{c.badge}</span>
            </div>
            
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4 leading-tight">
              {c.title}
            </h1>
            <p className="text-xl text-transparent bg-clip-text bg-gradient-to-r from-capsule-teal to-capsule-aqua">
              {c.subtitle}
            </p>
          </div>
        </div>
        
        {/* Bottom wave */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 60" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full">
            <path d="M0 60L48 55C96 50 192 40 288 35C384 30 480 30 576 33.3C672 37 768 43 864 45C960 47 1056 45 1152 41.7C1248 38 1344 33 1392 30.8L1440 29V60H1392C1344 60 1248 60 1152 60C1056 60 960 60 864 60C768 60 672 60 576 60C480 60 384 60 288 60C192 60 96 60 48 60H0Z" fill="#E7EAED"/>
          </svg>
        </div>
      </div>

      {/* FAQs */}
      <div className="max-w-4xl mx-auto px-4 py-16">
        <div className="space-y-4">
          {c.faqs.map((faq, index) => (
            <div
              key={index}
              className="glass-card rounded-2xl overflow-hidden transition-all duration-300 hover:shadow-glass-hover"
              data-testid={`faq-item-${index}`}
            >
              <button
                onClick={() => toggleFAQ(index)}
                className="w-full px-6 py-5 text-left flex items-center justify-between hover:bg-white/50 transition-colors"
                data-testid={`faq-toggle-${index}`}
              >
                <div className="flex items-center gap-4">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 transition-all duration-300 ${
                    openIndex === index 
                      ? 'bg-gradient-to-br from-capsule-teal to-capsule-aqua shadow-teal-glow' 
                      : 'bg-gray-100'
                  }`}>
                    <MessageCircleQuestion className={`w-5 h-5 ${openIndex === index ? 'text-white' : 'text-gray-500'}`} />
                  </div>
                  <span className="font-semibold text-gray-900 pr-4">{faq.question}</span>
                </div>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 transition-all duration-300 ${
                  openIndex === index ? 'bg-capsule-teal text-white rotate-180' : 'bg-gray-100 text-gray-500'
                }`}>
                  <ChevronDown className="w-5 h-5" />
                </div>
              </button>
              <div className={`transition-all duration-300 ease-in-out ${
                openIndex === index ? 'max-h-[1000px] opacity-100' : 'max-h-0 opacity-0 overflow-hidden'
              }`}>
                <div className="px-6 pb-6 pl-20">
                  <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default FAQsPage;
